import type { FilterOptionGroup } from "@outpave/ds";
import TransactionStatus from "enums/TransactionStatus";
import { transactionStatusLabel } from "components/common/RenderTransactionStatus";
import { PERSONAL_SPEND_STATUSES } from "utils/transactions/transactionGroups";

/** One home per status: a status under two headings would show twice while the filter is searched. */
const STATUS_HEADINGS: ReadonlyArray<{
  label: string;
  statuses: readonly TransactionStatus[];
}> = [
  {
    label: "In progress",
    statuses: [
      TransactionStatus.PENDING,
      TransactionStatus.PENDING_PAYMENT,
      TransactionStatus.PENDING_BANK_ACCOUNT,
      TransactionStatus.PROCESSING_PAYMENT,
    ],
  },
  {
    label: "Posted",
    statuses: [TransactionStatus.COMPLETE, TransactionStatus.CLOSED],
  },
  {
    label: "Refunds",
    statuses: [
      TransactionStatus.REFUNDED,
      TransactionStatus.REFUND_SUCCESS,
      TransactionStatus.PARTIALLY_REFUNDED,
      TransactionStatus.REFUND_PENDING,
      TransactionStatus.REFUND_FAIL,
      TransactionStatus.REFUND_CANCELED,
    ],
  },
  {
    label: "Not completed",
    statuses: [
      TransactionStatus.DECLINED,
      TransactionStatus.FAILED,
      TransactionStatus.CANCELED,
      TransactionStatus.REVERSED,
      TransactionStatus.RETURNED,
      TransactionStatus.PAYMENT_FAIL,
      TransactionStatus.INSUFFICIENT_FUNDS,
    ],
  },
  { label: "Personal", statuses: PERSONAL_SPEND_STATUSES },
];

export interface StatusFilter {
  /** Headings over one option per label, so statuses that read the same (two read "Refunded") are one option. */
  options: FilterOptionGroup[];
  /** The statuses behind the chosen options, whose values are labels. */
  statusesFor: (labels: readonly string[]) => TransactionStatus[];
}

/** The Status filter for a list offering these statuses, shared by /admin and /my so both read the same. */
export const statusFilter = (
  offered: readonly TransactionStatus[],
  translate: (key: string) => string,
): StatusFilter => {
  const byLabel = new Map<string, TransactionStatus[]>();
  offered.forEach((status) => {
    const label = transactionStatusLabel(status);
    byLabel.set(label, [...(byLabel.get(label) ?? []), status]);
  });

  const placed = new Set<string>();
  const groups = STATUS_HEADINGS.map(({ label, statuses }) => ({
    label,
    labels: statuses.map(transactionStatusLabel).filter((optionLabel) => {
      if (!byLabel.has(optionLabel) || placed.has(optionLabel)) return false;
      placed.add(optionLabel);
      return true;
    }),
  }));
  // Nothing is ever dropped: a status no heading names still lists, under Other.
  groups.push({
    label: "Other",
    labels: [...byLabel.keys()].filter((label) => !placed.has(label)),
  });

  return {
    options: groups
      .filter((group) => group.labels.length > 0)
      .map((group) => ({
        label: translate(group.label),
        options: group.labels.map((label) => ({
          value: label,
          label: translate(label),
        })),
      })),
    statusesFor: (labels) =>
      labels.flatMap((label) => byLabel.get(label) ?? []),
  };
};

/** The option values that cover these statuses, for a link that names statuses rather than labels. */
export const statusFilterLabels = (statuses: readonly string[]): string[] => [
  ...new Set(
    statuses.map((status) =>
      transactionStatusLabel(status as TransactionStatus),
    ),
  ),
];
